import { useState } from "react";
import { X, Sparkles } from "lucide-react";

interface InsightChip {
  question: string;
  answer: string;
  label?: string;
}


interface MetricInsightChipsProps {
  insights: InsightChip[];
  onOpenAIChat?: (topic?: string) => void;
  maxChips?: number;
}

// Shared AI identity orb — keep identical to the sidebar and the Ask Upmode bar.
function MiniOrb() {
  return (
    <span
      style={{
        width: 13,
        height: 13,
        borderRadius: 9999,
        background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))",
        flexShrink: 0,
      }}
    />
  );
}

export function MetricInsightChips({ insights, onOpenAIChat, maxChips = 3 }: MetricInsightChipsProps) {
  const [selectedInsight, setSelectedInsight] = useState<InsightChip | null>(null);
  const visible = insights.slice(0, maxChips);

  return (
    <>
      <div className="flex items-center gap-2" style={{ marginTop: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        {/* Ask Upmode lead — the orb doorway to the assistant */}
        {onOpenAIChat && (
          <button
            onClick={() => onOpenAIChat()}
            className="transition-all"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              background: 'transparent',
              border: 'none',
              padding: '0 4px',
              cursor: 'pointer',
              color: 'var(--spectral-violet)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--text-xs)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <MiniOrb />
            Ask Upmode
          </button>
        )}

        {visible.map((insight, index) => (
          <button
            key={index}
            onClick={() => setSelectedInsight(insight)}
            className="transition-all"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 30,
              padding: '0 13px',
              borderRadius: 9999,
              backgroundColor: 'var(--card)',
              color: 'var(--foreground)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--text-xs)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--spectral-violet)';
              e.currentTarget.style.color = 'var(--spectral-violet)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.color = 'var(--foreground)';
            }}
          >
            {insight.label ?? insight.question}
          </button>
        ))}
      </div>

      {/* Modal for insight answer */}
      {selectedInsight && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setSelectedInsight(null)}
        >
          <div
            className="relative rounded-lg p-8 shadow-xl"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              maxWidth: '560px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedInsight(null)}
              className="absolute top-4 right-4 p-2 rounded-lg transition-colors"
              style={{
                color: 'var(--muted-foreground)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--muted)';
                e.currentTarget.style.color = 'var(--foreground)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--muted-foreground)';
              }}
            >
              <X size={20} />
            </button>

            {/* Content */}
            <div className="space-y-6">
              {/* Question */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: 'var(--muted)',
                      color: 'var(--signal-blue)',
                    }}
                  >
                    <Sparkles size={16} />
                  </div>
                  <h3 style={{ color: 'var(--foreground)' }}>
                    {selectedInsight.question}
                  </h3>
                </div>
              </div>

              {/* Answer */}
              <div
                className="rounded-lg p-4"
                style={{
                  backgroundColor: 'var(--muted)',
                  border: '1px solid var(--border)',
                }}
              >
                <p style={{ color: 'var(--foreground)', lineHeight: '1.6' }}>
                  {selectedInsight.answer}
                </p>
              </div>

              {/* AI Chat Button */}
              {onOpenAIChat && (
                <button
                  onClick={() => onOpenAIChat(selectedInsight.question)}
                  className="w-full rounded-lg px-4 py-3 transition-colors flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: 'var(--accent-foreground)',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  Continue this conversation with AI →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}